'use client';

// [Dialog Manager]
// Dialog Manager will only show one Dialog at a time
// requires a <DialogHost> child to render somewhere in the hierarchy

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useSoundManager } from './sound/soundmanager';
import SoundId from './sound/soundid';
import { DialogHostScope } from '@/src/enum/dialog';

/**
 * Represents an entry of a Dialog in the queue
 */
type DialogEntry = {
    /**
     * Unique symbol for the Dialog. Using this in addition
     * to key since symbols are not guessable.
     */
    id: symbol;

    /**
     * Controls if the dialog is open
     * Since there's an animation that plays when changing isOpen,
     * we want to make sure we play this before removing the Dialog
     */
    isOpen: boolean;

    /**
     * Key for React to disambiguate dialogs. This monotonically increases
     */
    key: number;

    /**
     * Original JSX element of the dialog, we clone this later to inject props
     */
    dialog: JSX.Element;

    /**
     * The scope to render this dialog. For example, there is a "Game" scope
     * that is mounted in play.tsx so that dialogs that need game information
     * can access it via context.
     */
    scope?: DialogHostScope;
};

type DialogManagerContextProps = {
    addDialog: (dialog: JSX.Element, scope?: DialogHostScope) => void;
    onCloseDialog: () => void;
    currentDialog: JSX.Element | null
    currentDialogEntry: DialogEntry | undefined;
}

const DialogManagerContext = createContext<DialogManagerContextProps>({
    addDialog: () => { },
    onCloseDialog: () => { },
    currentDialog: null,
    currentDialogEntry: undefined,
});

// monotonically increasing key
let key = 0;

export default function DialogManager({ children }) {
    const [dialogsState, setDialogsState] = useState<DialogEntry[]>([]);
    const { playSound } = useSoundManager();

    /** adds a dialog to the queue */
    const addDialog = useCallback(
        (dialog: JSX.Element, scope?: DialogHostScope): void => {
            setDialogsState(
                (oldState) => [
                    ...oldState,
                    {
                        id: Symbol(),
                        isOpen: true,
                        key: ++key,
                        dialog,
                        scope,
                    },
                ],
            );
        },
        [setDialogsState],
    );

    // legacy call to close the dialog, need to move off of this
    const onCloseDialog = useCallback(
        () => {
            setDialogsState(
                (oldState) => oldState.slice(1),
            );
        },
        [setDialogsState],
    );

    /**
     * closes a dialog by its unique id. it will set isOpen to false to play the
     * closing animation then remove it from the queue after a short delay.
     * it also handles playing the "close" popup sound
     */
    const closeDialogById = useCallback(
        (id: symbol) => {
            playSound(SoundId.Close);

            setDialogsState(
                (oldState) => oldState.map(
                    (dialogEntryIterator) => {
                        if (dialogEntryIterator.id === id) {
                            return {
                                ...dialogEntryIterator,
                                isOpen: false,
                            };
                        }

                        return dialogEntryIterator;
                    }
                ),
            );

            setTimeout(
                () => setDialogsState(
                    (oldState) => oldState.filter(
                        (dialogEntryIterator) => dialogEntryIterator.id !== id,
                    ),
                ),
                300,
            );
        },
        [playSound, setDialogsState],
    );

    /**
     * returns the current dialog to view
     * we need to clone it to inject the key, isOpen and onClose props dynamically
     * but we keep the key the same to avoid rerenders
     */
    const currentDialog = useMemo(
        () => {
            if (dialogsState.length === 0) {
                return null;
            }

            const dialogEntry = dialogsState[0];

            return React.cloneElement(
                dialogEntry.dialog,
                {
                    key: dialogEntry.key,
                    isOpen: dialogEntry.isOpen,
                    onClose: () => closeDialogById(dialogEntry.id),
                },
            );
        },
        [dialogsState, closeDialogById],
    );

    // expose the current entry for DialogHosts to render
    const currentDialogEntry = useMemo(
        (): DialogEntry | undefined => dialogsState[0],
        [dialogsState],
    );

    const value: DialogManagerContextProps = useMemo(
        () => ({
            addDialog,
            currentDialog,
            currentDialogEntry,
            onCloseDialog,
        }),
        [addDialog, currentDialog, currentDialogEntry, onCloseDialog],
    );

    return (
        <DialogManagerContext.Provider value={value}>
            <>
                {children}
            </>
        </DialogManagerContext.Provider>
    );
}

export function useDialogManager() {
    return useContext(DialogManagerContext);
}
